import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Box } from '@mui/material';
import { ToastCard } from './ToastCard';
import { toastStore } from './toast-store';
import { ToastItem, ToastPosition } from './toast-types';

export interface ToastContainerProps {
  position?: ToastPosition;
  className?: string;
  limit?: number;
  autoClose?: number | false;
  hideProgressBar?: boolean;
  newestOnTop?: boolean;
  closeOnClick?: boolean;
  rtl?: boolean;
  pauseOnFocusLoss?: boolean;
  draggable?: boolean;
  pauseOnHover?: boolean;
  theme?: 'light' | 'dark' | 'colored';
  [key: string]: any;
}

const getPositionSx = (pos: ToastPosition) => {
  switch (pos) {
    case 'top-left':
      return { top: 20, left: 20, alignItems: 'flex-start' };
    case 'top-center':
      return { top: 20, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' };
    case 'top-right':
      return { top: 20, right: 20, alignItems: 'flex-end' };
    case 'bottom-left':
      return { bottom: 20, left: 20, alignItems: 'flex-start' };
    case 'bottom-center':
      return { bottom: 24, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' };
    case 'bottom-right':
      return { bottom: 24, right: 24, alignItems: 'flex-end' };
  }
};

export const ToastContainer: React.FC<ToastContainerProps> = ({
  position = 'top-right',
  className = '',
  limit,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const unsubscribe = toastStore.subscribe((newToasts) => {
      setToasts(newToasts);
    });
    return () => unsubscribe();
  }, []);

  const handleDismiss = (id: string | number) => {
    toastStore.dismiss(id);
  };

  // Group toasts by position
  const positions: ToastPosition[] = [
    'top-left',
    'top-center',
    'top-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ];

  return (
    <Box sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 99999, overflow: 'hidden' }}>
      {positions.map((pos) => {
        const activeToastsForPos = toasts.filter(
          (t) => (t.position || position) === pos,
        );

        if (activeToastsForPos.length === 0) return null;

        const visibleToasts = limit
          ? activeToastsForPos.slice(-limit)
          : activeToastsForPos;

        return (
          <Box
            key={pos}
            className={className}
            sx={{
              position: 'fixed',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              maxWidth: '100%',
              p: 2,
              pointerEvents: 'none',
              ...getPositionSx(pos),
            }}
          >
            <AnimatePresence initial={false}>
              {visibleToasts.map((toastItem) => (
                <ToastCard
                  key={toastItem.id}
                  toast={toastItem}
                  onDismiss={handleDismiss}
                />
              ))}
            </AnimatePresence>
          </Box>
        );
      })}
    </Box>
  );
};

// Aliases
export const Toaster = ToastContainer;
export const NotificationContainer = ToastContainer;
export default ToastContainer;
